'use client'

import React, { createContext, useContext, useState, useEffect } from 'react'

export type ThemeMode = 'light' | 'dark'

interface ThemeContextType {
  theme: ThemeMode
  setTheme: (theme: ThemeMode) => void
  toggleTheme: () => void
  accentColor: string // Hex color for accent
  setAccentColor: (color: string) => void
  accentHsl: { h: number; s: number; l: number } // HSL for Three.js animations
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Green/teal for dark theme, orange for light theme
const getDefaultAccentColor = (themeMode: ThemeMode): string => 
  themeMode === 'dark' ? '#2dd4bf' : '#fb923c' // teal-400 for dark, orange-400 for light

// Convert hex to HSL
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }

  return { h, s, l }
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>('dark')
  const [accentColor, setAccentColorState] = useState<string>('#2dd4bf') // Default to green for dark
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {

    const savedTheme = localStorage.getItem('theme-mode') as ThemeMode | null
    
    if (savedTheme === 'light' || savedTheme === 'dark') {
      setThemeState(savedTheme)
      setAccentColorState(getDefaultAccentColor(savedTheme))
    } else {
      // Default to dark theme with green
      setThemeState('dark')
      setAccentColorState(getDefaultAccentColor('dark'))
    }
    
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!hydrated) return
    const themeBasedColor = getDefaultAccentColor(theme)
    setAccentColorState(themeBasedColor)

    const accentHsl = hexToHsl(themeBasedColor)
    const root = document.documentElement
    root.style.setProperty('--accent-color', themeBasedColor)
    root.style.setProperty('--accent-hue', accentHsl.h.toString())
    root.style.setProperty('--accent-saturation', accentHsl.s.toString())
    root.style.setProperty('--accent-lightness', accentHsl.l.toString())
    root.classList.remove('light', 'dark')
    root.classList.add(theme)
    localStorage.setItem('theme-mode', theme)
    localStorage.setItem('accent-color', themeBasedColor)
  }, [theme, hydrated])

  useEffect(() => {
    if (!hydrated) return

    const accentHsl = hexToHsl(accentColor)
    
    const root = document.documentElement
    root.style.setProperty('--accent-color', accentColor)
    root.style.setProperty('--accent-hue', accentHsl.h.toString())
    root.style.setProperty('--accent-saturation', accentHsl.s.toString())
    root.style.setProperty('--accent-lightness', accentHsl.l.toString())
    
    localStorage.setItem('accent-color', accentColor)
  }, [accentColor, hydrated])

  useEffect(() => {
    const root = document.documentElement
    const savedTheme = localStorage.getItem('theme-mode') as ThemeMode | null
    const initialTheme = savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : 'dark'
    const initialAccentColor = getDefaultAccentColor(initialTheme)
    const initialHsl = hexToHsl(initialAccentColor)
    
    root.style.setProperty('--accent-color', initialAccentColor)
    root.style.setProperty('--accent-hue', initialHsl.h.toString())
    root.style.setProperty('--accent-saturation', initialHsl.s.toString())
    root.style.setProperty('--accent-lightness', initialHsl.l.toString())
    root.classList.add(initialTheme)
  }, [])

  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme)
  }

  const toggleTheme = () => {
    setThemeState(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light'
      const newAccentColor = getDefaultAccentColor(newTheme)
      setAccentColorState(newAccentColor)
      return newTheme
    })
  }

  const setAccentColor = (color: string) => {
    setAccentColorState(color)
  }

  if (!hydrated) return null

  const currentAccentColor = accentColor || getDefaultAccentColor(theme)
  
  return (
    <ThemeContext.Provider 
      value={{ 
        theme, 
        setTheme, 
        toggleTheme,
        accentColor: currentAccentColor, 
        setAccentColor,
        accentHsl: hexToHsl(currentAccentColor)
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

